import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/solid';
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import OptionMenu from 'components/OptionMenu';
import { Link } from 'react-router-dom';
import { withRequestReturnTo } from 'utils/requestNavigation';

type OrdersTableprops = {
  columns: {
    id: number | string;
    name?: string;
    key?: string;
    render?: (item: any, method?: any) => JSX.Element;
    value?: string;
  }[];
  orders: any[];
  checkbox?: boolean;
  onSelectAll?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectedRow?: (item: any) => void;
  allChecked?: any[];
  emptyMessage?: string;
  orderByOptions?: string[];
  handleSelectedColumn?: (column: string, direction: string) => void;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
};

export const OrdersTable = ({
  columns,
  orders,
  checkbox,
  handleSelectedRow,
  onSelectAll,
  allChecked,
  emptyMessage,
  orderByOptions,
  handleSelectedColumn,
  sortColumn,
  sortDirection,
}: OrdersTableprops) => {
  const [internalSelectedColumn, setInternalSelectedColumn] = useState<
    string | null
  >(null);
  const [internalDirection, setInternalDirection] = useState<'asc' | 'desc'>(
    'desc',
  );
  const selectedColumn = sortColumn ?? internalSelectedColumn;
  const direction = sortDirection ?? internalDirection;

  const handleSelectColumn = (column: string | undefined) => {
    if (!column || !orderByOptions?.includes(column)) return;
    const nextDirection =
      column === selectedColumn && direction === 'asc' ? 'desc' : 'asc';
    if (sortColumn === undefined) setInternalSelectedColumn(column);
    if (sortDirection === undefined) setInternalDirection(nextDirection);
    handleSelectedColumn?.(column, nextDirection);
  };

  useEffect(() => {
    if (
      sortColumn === undefined &&
      !internalSelectedColumn &&
      orderByOptions &&
      orderByOptions.length > 0
    ) {
      setInternalSelectedColumn(orderByOptions[0]);
    }
  }, [internalSelectedColumn, orderByOptions, sortColumn]);

  const totalColumns = columns.length + 2 + (checkbox ? 1 : 0);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div
        role="region"
        aria-label="Order results"
        tabIndex={0}
        className="overflow-x-auto focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
      >
        <table
          aria-label="All orders"
          className="min-w-full divide-y divide-gray-200 text-left text-sm"
        >
          <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              {checkbox && (
                <th scope="col" className="w-12 px-4 py-3.5 text-left">
                  <input
                    type="checkbox"
                    aria-label="Select all orders"
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    onChange={onSelectAll}
                  />
                </th>
              )}
              <th
                scope="col"
                aria-label="Order number"
                className="sticky left-0 z-20 min-w-[150px] border-r border-gray-200 bg-gray-50 px-4 py-3.5"
              >
                <div className="flex items-center gap-2">
                  <span>Order #</span>
                </div>
              </th>
              {columns.map((column) => {
                const sortable = Boolean(
                  column.value && orderByOptions?.includes(column.value),
                );
                const active = sortable && selectedColumn === column.value;
                return (
                  <th
                    key={column.id}
                    scope="col"
                    aria-label={column.name}
                    aria-sort={
                      sortable
                        ? active
                          ? direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                        : undefined
                    }
                    className="whitespace-nowrap px-4 py-3.5"
                  >
                    {sortable ? (
                      <button
                        type="button"
                        aria-label={`Sort by ${column.name}`}
                        onClick={() => handleSelectColumn(column.value)}
                        className={classNames(
                          'inline-flex items-center gap-1 rounded text-left font-semibold uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                          active
                            ? 'text-blue-700'
                            : 'text-gray-500 hover:text-gray-800',
                        )}
                      >
                        <span aria-hidden="true">{column.name}</span>
                        {active &&
                          (direction === 'asc' ? (
                            <ChevronUpIcon
                              className="h-3.5 w-3.5"
                              aria-hidden="true"
                            />
                          ) : (
                            <ChevronDownIcon
                              className="h-3.5 w-3.5"
                              aria-hidden="true"
                            />
                          ))}
                      </button>
                    ) : (
                      column.name
                    )}
                  </th>
                );
              })}
              <th
                scope="col"
                className="min-w-[110px] border-l border-gray-200 bg-gray-50 px-4 py-3.5 text-center md:sticky md:right-0 md:z-20"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {orders.map((item: any, i: number) => (
              <React.Fragment key={item.id || i}>
                <tr className="group align-top transition-colors hover:bg-blue-50">
                  {checkbox && (
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      <input
                        name="comments"
                        type="checkbox"
                        aria-label={`Select order ${item.number || item.id}`}
                        checked={Boolean(
                          allChecked && allChecked.includes(item.id),
                        )}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        onChange={() => handleSelectedRow?.(item)}
                      />
                    </td>
                  )}
                  <td className="sticky left-0 z-10 min-w-[150px] border-r border-gray-200 bg-white px-4 py-3 group-hover:bg-blue-50">
                    <div className="flex items-center gap-2">
                      <Link
                        className="whitespace-nowrap rounded font-semibold text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        to={withRequestReturnTo(
                          `/orders/${item.id}`,
                          item.returnLocation || '/orders',
                        )}
                      >
                        {item.number}
                      </Link>
                    </div>
                  </td>
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-600"
                    >
                      {column.render ? column.render(item) : null}
                      {column.key ? item[column.key] : null}
                    </td>
                  ))}
                  <td className="min-w-[110px] border-l border-gray-200 bg-white px-4 py-2 text-center text-sm font-medium text-gray-600 group-hover:bg-blue-50 md:sticky md:right-0 md:z-10">
                    <div className="flex justify-center">
                      <OptionMenu
                        triggerLabel={`Actions for order ${
                          item.number || item.id
                        }`}
                        options={[
                          { label: 'View', onClick: item.onViewOrder },
                          item.protectionTotal || item.isReorder
                            ? {
                                label: 'File issue',
                                onClick: item.onFileClaim,
                              }
                            : null,
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
            {orders.length < 1 && (
              <tr>
                <td
                  colSpan={totalColumns}
                  className="px-4 py-10 text-center text-sm text-gray-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
